import React from "react";
import { Link } from "react-router-dom";
import LegalPageLayout from "./LegalPageLayout";

const RefundPolicy = () => {
  document.title = "AMANAT - Refund Policy";

  return (
    <LegalPageLayout>
      <div className="legal-doc-header">
        <p className="legal-doc-brand">AMANAT</p>
        <p className="legal-doc-company">
          Colca Services W.L.L · CR No. 178727-1 · Kingdom of Bahrain
        </p>
        <h1 className="legal-doc-title">RETURN &amp; REFUND POLICY</h1>
        <p className="legal-doc-version">Version 1.0</p>
        <p className="legal-doc-dates">Effective Date: 1 May 2026</p>
        <p className="legal-doc-dates">
          Issued in compliance with: MOICT Activity 4791 (CR No. 178727-1) ·
          Consumer Protection Law No. 35/2012 · CBB Consumer Protection Module ·
          Bahrain Contract Law
        </p>
      </div>

      <h2>1. Introduction</h2>
      <p>
        This Return &amp; Refund Policy (&quot;Policy&quot;) is issued by Colca Services
        W.L.L, trading as Amanat (&quot;Amanat&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;), in compliance
        with the following regulations and laws:
      </p>
      <ul>
        <li>
          <strong>MOICT Activity 4791 (CR No. 178727-1):</strong> eCommerce/eCart
          requirements.
        </li>
        <li>
          <strong>Consumer Protection Law No. 35/2012:</strong> Returns, defective
          goods, and supplier obligations.
        </li>
        <li>
          <strong>CBB Consumer Protection Module:</strong> Consumer protection and
          applicable regulatory requirements.
        </li>
        <li>
          <strong>Bahrain Contract Law:</strong> Contractual and commercial legal
          requirements applicable in the Kingdom of Bahrain.
        </li>
      </ul>
      <p>
        This Policy applies to all Product and Service purchases made through
        Amanat&apos;s eCart (
        <a
          href="https://ecart.amanatfintech.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          ecart.amanatfintech.com
        </a>
        ) and Amanat Mobile Application.
      </p>
      <p>
        This Policy is available in both Arabic and English. In the event of any
        conflict, the Arabic version shall prevail in accordance with the laws of the
        Kingdom of Bahrain. Arabic version:{" "}
        <a
          href="https://www.amanatfintech.com/refund-policy/ar"
          target="_blank"
          rel="noopener noreferrer"
        >
          www.amanatfintech.com/refund-policy/ar
        </a>
      </p>

      <h2>2. Scope of This Policy</h2>
      <p>
        This Policy covers all Products and Services offered by Amanat through the
        eCart and Mobile Application, including digital products, subscription-based
        services, and any other paid offerings made available by Amanat from time to
        time.
      </p>
      <p>
        Free features and tools within the Amanat Application are not subject to this
        Policy. To report a technical issue with a free feature, please contact:{" "}
        <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a>.
      </p>

      <h2>3. Cooling-Off Period &amp; General Refund Conditions</h2>
      <h3>3.1 Cooling-Off Period</h3>
      <p>
        You are entitled to cancel your purchase and receive a full refund within ten
        (10) calendar days from the date of purchase (&quot;Cooling-Off Period&quot;),
        provided that:
      </p>
      <ul>
        <li>
          The cancellation request is submitted within 10 calendar days of the purchase
          date; and
        </li>
        <li>
          You have not substantially consumed, downloaded, or used the Product or
          Service; and
        </li>
        <li>
          No completion certificate, licence key, or equivalent proof of delivery has
          been issued.
        </li>
      </ul>
      <p>
        The 10-calendar-day Cooling-Off Period is consistent with the CBB Consumer
        Protection Module and applicable consumer protection principles in the Kingdom
        of Bahrain for distance contracts. Amanat commits to honoring this period for
        all eligible Products and Services.
      </p>

      <h3>3.2 Non-Refundable Circumstances</h3>
      <p>Refunds will not be granted outside the Cooling-Off Period where:</p>
      <ul>
        <li>
          The cancellation request is submitted more than 10 calendar days after the
          purchase date;
        </li>
        <li>
          The Product or Service has been substantially consumed or accessed (more than
          20%) or downloaded;
        </li>
        <li>
          A completion certificate, access credentials, or licence has been issued and
          used;
        </li>
        <li>
          The Product was purchased at a promotional or discounted rate, unless the
          Product is defective (see clause 3.3).
        </li>
      </ul>

      <h3>3.3 Defective or Misdescribed Products (Statutory Right — Cannot be Waived)</h3>
      <p>
        Notwithstanding the limitations in clause 3.2, you are entitled to a full refund
        if the Product or Service is:
      </p>
      <ul>
        <li>Materially defective or fails to function as described;</li>
        <li>Inaccessible due to a technical error on Amanat&apos;s part;</li>
        <li>Materially different from its description at the time of purchase.</li>
      </ul>
      <p>
        This right applies regardless of usage percentage, time elapsed since purchase,
        or whether the Product was purchased at a promotional price. This right is
        guaranteed under Bahrain Consumer Protection Law No. 35/2012 (Articles 11 and
        12) and cannot be excluded by any clause in this Policy or the Terms and
        Conditions.
      </p>

      <h3>3.4 Product Discontinuation</h3>
      <p>
        If Amanat discontinues a Product or Service after purchase and before full
        consumption:
      </p>
      <ul>
        <li>You receive a full pro-rata refund for the unconsumed portion.</li>
        <li>
          Amanat will provide a minimum of 14 calendar days&apos; advance notice before
          discontinuing any active Product or Service.
        </li>
      </ul>

      <h3>3.5 Technical Access Failure</h3>
      <p>
        If a charge is successfully deducted but access to the Product or Service is not
        granted due to a technical error on Amanat&apos;s part:
      </p>
      <ul>
        <li>
          Amanat will restore access within 24 hours of the issue being reported; or
        </li>
        <li>
          Issue a full refund if access cannot be restored within 24 hours.
        </li>
        <li>
          Access failures must be reported electronically to{" "}
          <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a>{" "}
          within 48 hours of purchase.
        </li>
      </ul>

      <h2>4. How to Request a Refund — Online Only</h2>
      <p>
        Refund requests must be submitted electronically through one of the following
        channels:
      </p>
      <ul>
        <li>
          <strong>Email:</strong>{" "}
          <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a>{" "}
          (Subject line: &apos;Refund Request – [Order Reference Number]&apos;)
        </li>
        <li>
          <strong>In-App:</strong> Settings → Help &amp; Support → Request a Refund
        </li>
      </ul>
      <p>Please include the following information in your request:</p>
      <ul>
        <li>Full name and registered email address</li>
        <li>Order reference number</li>
        <li>Product or Service name or description</li>
        <li>Reason for the refund request</li>
        <li>
          Supporting evidence where applicable (e.g., screenshot showing the error or
          access failure)
        </li>
      </ul>
      <p>
        <strong>Response Times:</strong> Amanat will acknowledge receipt of your refund
        request within 2 business days of receipt. A decision (approval or decline with
        reasons) will be communicated within 5 business days. If declined, you may
        escalate as set out in clause 7.
      </p>
      <p>
        <strong>Note:</strong> Amanat does not accept refund requests by telephone or in
        person. All requests must be submitted through the online channels above to
        ensure proper record-keeping.
      </p>

      <h2>5. Refund Processing Time</h2>
      <p>
        Once a refund is approved, the amount will be returned to the original payment
        method used at the time of purchase:
      </p>
      <ul>
        <li>
          <strong>Visa / Mastercard (credit or prepaid card):</strong> 7–14 business days
          (subject to bank processing times)
        </li>
        <li>
          <strong>BenefitPay:</strong> 3–7 business days
        </li>
      </ul>
      <p>
        Amanat processes approved refunds promptly. The actual time for the amount to
        appear in your account depends on your card issuer or payment provider, which is
        outside Amanat&apos;s control. Refunds are returned to the original payment
        method only; they cannot be redirected to a different card, account, or payment
        method.
      </p>

      <h2>6. Payment Methods &amp; Special Cases</h2>
      <h3>6.1 Accepted Payment Methods</h3>
      <p>
        Amanat&apos;s eCart accepts Visa, Mastercard, and BenefitPay. Cash on delivery is
        not available. All purchases must be completed using the secure electronic payment
        methods listed above.
      </p>
      <h3>6.2 Card Disputes (Chargeback)</h3>
      <p>
        If you initiate a chargeback with your card issuer or BenefitPay without first
        contacting Amanat, we will handle the dispute through the relevant payment
        provider. We strongly encourage customers to contact Amanat directly at{" "}
        <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a> before
        raising a chargeback, as parallel proceedings may delay resolution.
      </p>
      <h3>6.3 Failed Transactions</h3>
      <p>
        If an amount has been deducted from your account but you have not received an
        order confirmation within 30 minutes, please report this immediately online to{" "}
        <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a>. Amanat
        will investigate and confirm your order or issue a full refund within 3 business
        days. Amanat is not liable for any delays by the bank or card issuer in reversing
        a failed transaction.
      </p>

      <h2>7. Dispute Resolution &amp; Escalation</h2>
      <p>
        If you are dissatisfied with the outcome of your refund request, you may escalate
        your complaint through the following stages. Each stage should be exhausted before
        proceeding to the next:
      </p>

      <h3>Stage 1 — Internal Complaints Resolution (First Point of Contact)</h3>
      <p>
        Submit your complaint directly to Amanat&apos;s Complaints Resolution Team. We are
        committed to handling all complaints fairly and in a timely manner.
      </p>
      <ul>
        <li>
          <strong>Online Form / Email:</strong>{" "}
          <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a> |{" "}
          <strong>Phone:</strong>{" "}
          <a href="tel:+97336961804">+973 3696 1804</a>
        </li>
        <li>
          <strong>Subject line:</strong> &apos;Formal Complaint – [Name] – [Order
          Reference]&apos;
        </li>
        <li>
          <strong>Response Times:</strong> Acknowledgement within 2 business days;
          Resolution within 5 business days.
        </li>
      </ul>
      <p>
        If your complaint is not resolved to your satisfaction at Stage 1 within 5
        business days, you may escalate to the relevant external authority at Stage 2.
      </p>

      <h3>
        Stage 2 — Ministry of Industry, Commerce &amp; Tourism — eCommerce Division
        (Mandatory External Authority — eCommerce Licence)
      </h3>
      <p>
        For complaints relating to eCommerce transactions, product descriptions, delivery,
        or refusal of refund, you may escalate to MOICT — the authority that licences
        Amanat&apos;s eCommerce activity (Activity 4791, CR No. 178727-1). Disclosure of
        MOICT at this stage is a mandatory disclosure under Amanat&apos;s eCommerce
        licence.
      </p>
      <ul>
        <li>
          <strong>Email:</strong>{" "}
          <a href="mailto:ecommerce@moic.gov.bh">ecommerce@moic.gov.bh</a>
        </li>
        <li>
          <strong>Tel:</strong> <a href="tel:17359008">17359008</a>
        </li>
        <li>
          <strong>Consumer Protection Directorate:</strong>{" "}
          <a href="tel:17574969">17574969</a>
        </li>
      </ul>

      <h3>
        Stage 3 — Central Bank of Bahrain (Mandatory External Authority — Regulated
        Financial Services)
      </h3>
      <p>
        For complaints relating to CBB-supervised financial services (including any
        CBB-licensed activity conducted by Amanat), you may escalate to the CBB Consumer
        Protection Unit. Disclosure of this right is mandatory under the CBB Consumer
        Protection Module.
      </p>
      <ul>
        <li>
          <strong>Website:</strong>{" "}
          <a href="https://www.cbb.gov.bh" target="_blank" rel="noopener noreferrer">
            www.cbb.gov.bh
          </a>
        </li>
        <li>
          <strong>Online Complaints Portal:</strong>{" "}
          <a
            href="https://complaints.cbb.gov.bh"
            target="_blank"
            rel="noopener noreferrer"
          >
            complaints.cbb.gov.bh
          </a>
        </li>
        <li>
          <strong>Phone:</strong>{" "}
          <a href="tel:+97317547789">+973 17547789</a>
        </li>
      </ul>
      <p>
        <strong>Note on Personal Data:</strong> For any query specifically relating to the
        processing of your personal data during the handling of your refund request,
        please contact our Privacy Officer at{" "}
        <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a> or refer
        to our Privacy Policy at{" "}
        <Link to="/privacy-policy">www.amanatfintech.com/privacy-policy</Link>.
      </p>

      <h2>8. Governing Law</h2>
      <p>
        This Policy is governed by the laws of the Kingdom of Bahrain, including Consumer
        Protection Law No. 35/2012, the Civil Code, and CBB regulations. Any disputes
        arising from this Policy shall be subject to the exclusive jurisdiction of the
        competent courts of the Kingdom of Bahrain.
      </p>

      <h2>9. Changes to This Policy</h2>
      <p>
        Amanat reserves the right to update this Policy at any time to reflect changes in
        our Products, Services, or applicable law. Material changes will be communicated
        via the website and in-app notification. The updated version will include a new
        &quot;Last Revised&quot; date. Your continued use of Amanat&apos;s services after
        the revised effective date constitutes acceptance of the revised Policy.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        <strong>Colca Services W.L.L (Amanat)</strong>
      </p>
      <p>
        CR No. 178727-1 | 302, 3rd Floor, Bahrain Fintech Bay, Arcapita Building 551, Road
        4612, Block 346, Manama, Kingdom of Bahrain
      </p>
      <ul>
        <li>
          <strong>Email:</strong>{" "}
          <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a> |{" "}
          <a href="mailto:info@amanatfintech.com">info@amanatfintech.com</a>
        </li>
        <li>
          <strong>Website:</strong>{" "}
          <a
            href="https://www.amanatfintech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.amanatfintech.com
          </a>{" "}
          | <strong>eCart:</strong>{" "}
          <a
            href="https://ecart.amanatfintech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ecart.amanatfintech.com
          </a>
        </li>
        <li>
          <strong>Business Hours:</strong> Sunday – Thursday, 9:00 AM – 5:00 PM (AST)
        </li>
      </ul>

      <h2>11. Bilingual Clause / البند ثنائي اللغة</h2>
      <p>
        This Return &amp; Refund Policy is prepared in both English and Arabic. In the
        event of any inconsistency or conflict between the English and Arabic versions,
        the Arabic version shall prevail in accordance with the laws of the Kingdom of
        Bahrain.
      </p>
      <p dir="rtl" className="legal-rtl">
        تم إعداد سياسة الإرجاع والاسترداد هذه باللغتين الإنجليزية والعربية. وفي حالة أي
        تناقض أو تعارض بين النسختين الإنجليزية والعربية، تسود النسخة العربية وفقاً لقوانين
        مملكة البحرين.
      </p>

      <hr className="legal-divider" />

      <div className="legal-rtl">
        <div className="legal-doc-header">
          <p className="legal-doc-brand">أمانات</p>
          <p className="legal-doc-company">
            كولكا للخدمات ذ.م.م · السجل التجاري رقم 178727-1 · مملكة البحرين
          </p>
          <h1 className="legal-doc-title">سياسة الإرجاع والاسترداد</h1>
          <p className="legal-doc-version">الإصدار 1.0</p>
          <p className="legal-doc-dates">تاريخ السريان: 1 مايو 2026</p>
          <p className="legal-doc-dates">
            صادرة وفقاً لـ: متطلبات وزارة الصناعة والتجارة والسياحة (MOICT) – النشاط 4791 ·
            قانون حماية المستهلك رقم 35/2012 · وحدة حماية المستهلك – مصرف البحرين المركزي ·
            قانون العقود البحريني
          </p>
        </div>

        <h2>1. المقدمة</h2>
        <p>
          تُصدر شركة كولكا سيرفيسز ذ.م.م، المعاملة تجارياً باسم &quot;أمانات&quot;
          (&quot;أمانات&quot;، &quot;نحن&quot;، &quot;لنا&quot;، &quot;بنا&quot;)، سياسة
          الإرجاع والاسترداد هذه (&quot;السياسة&quot;) امتثالاً للوائح والقوانين التالية:
        </p>
        <ul>
          <li>
            وزارة الصناعة والتجارة والسياحة – النشاط 4791 (السجل التجاري 178727-1): متطلبات
            التجارة الإلكترونية/eCart.
          </li>
          <li>
            قانون حماية المستهلك رقم 35/2012: حق إرجاع البضائع والبضائع المعيبة ومسؤوليات
            المورِّد.
          </li>
          <li>
            مصرف البحرين المركزي – وحدة حماية المستهلك: متطلبات حماية المستهلك واللوائح
            التنظيمية المعمول بها.
          </li>
          <li>
            قانون العقود البحريني: المتطلبات التعاقدية والتجارية المعمول بها في مملكة
            البحرين.
          </li>
        </ul>
        <p>
          تسري هذه السياسة على جميع مشتريات المنتجات والخدمات التي تتم عبر متجر أمانات
          الإلكتروني (eCart) على الرابط{" "}
          <a
            href="https://ecart.amanatfintech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ecart.amanatfintech.com
          </a>{" "}
          وتطبيق أمانات للجوال.
        </p>
        <p>
          تتوفر هذه السياسة باللغتين العربية والإنجليزية. في حالة وجود أي تعارض، تسود
          النسخة العربية وفقاً لقوانين مملكة البحرين. النسخة العربية:{" "}
          <a
            href="https://www.amanatfintech.com/refund-policy/ar"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.amanatfintech.com/refund-policy/ar
          </a>
        </p>

        <h2>2. نطاق هذه السياسة</h2>
        <p>
          تشمل هذه السياسة جميع المنتجات والخدمات التي تقدمها أمانات عبر متجر eCart
          الإلكتروني وتطبيق الجوال، بما في ذلك المنتجات الرقمية والخدمات القائمة على
          الاشتراك وأي عروض مدفوعة أخرى تتاحها أمانات من وقت لآخر.
        </p>
        <p>
          لا تخضع الميزات والأدوات المجانية داخل تطبيق أمانات لأحكام هذه السياسة. للإبلاغ عن
          أي مشكلة تقنية تتعلق بالميزات المجانية، يُرجى التواصل عبر:{" "}
          <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a>.
        </p>

        <h2>3. فترة التراجع وشروط الاسترداد العامة</h2>
        <h3>3.1 فترة التراجع</h3>
        <p>
          يحق لك إلغاء عملية الشراء واسترداد المبلغ كاملاً خلال عشرة (10) أيام تقويمية من
          تاريخ الشراء (&quot;فترة التراجع&quot;)، شريطة أن:
        </p>
        <ul>
          <li>يُقدَّم طلب الإلغاء خلال 10 أيام تقويمية من تاريخ الشراء؛</li>
          <li>
            لا تكون قد استهلكت أو نزَّلت أو استخدمت المنتج أو الخدمة بشكل جوهري؛
          </li>
          <li>
            لم يُصدر أي شهادة إتمام، مفتاح ترخيص، أو ما يعادلهما كإثبات على اكتمال التسليم.
          </li>
        </ul>
        <p>
          تتسق فترة التراجع البالغة 10 أيام تقويمية مع وحدة حماية المستهلك لدى مصرف البحرين
          المركزي ومبادئ حماية المستهلك المعمول بها في مملكة البحرين للعقود المبرمة عن
          بُعد. تلتزم أمانات بتطبيق هذه الفترة على جميع المنتجات والخدمات المؤهلة.
        </p>

        <h3>3.2 حالات عدم الاسترداد</h3>
        <p>لن يُقبل طلب الاسترداد خارج فترة التراجع في الحالات التالية:</p>
        <ul>
          <li>
            تقديم طلب الإلغاء بعد مرور أكثر من 10 أيام تقويمية على تاريخ الشراء؛
          </li>
          <li>
            الاستهلاك الجوهري للمنتج أو الخدمة أو الوصول إليها بنسبة تتجاوز 20%، أو تنزيلها؛
          </li>
          <li>إصدار شهادة إتمام أو بيانات اعتماد الوصول أو الترخيص واستخدامها؛</li>
          <li>
            شراء المنتج بسعر ترويجي أو مخفَّض، إلا إذا كان المنتج معيباً (انظر البند 3.3).
          </li>
        </ul>

        <h3>3.3 المنتجات المعيبة أو غير المطابقة للوصف (حق قانوني — لا يمكن التنازل عنه)</h3>
        <p>
          استثناءً من القيود الواردة في البند 3.2، يحق لك الحصول على استرداد كامل إذا كان
          المنتج أو الخدمة:
        </p>
        <ul>
          <li>معيباً بصورة جوهرية أو يفشل في العمل وفق وصفه؛</li>
          <li>غير قابل للوصول بسبب خطأ تقني من جانب أمانات؛</li>
          <li>مغايراً بصورة جوهرية لوصفه وقت الشراء.</li>
        </ul>
        <p>
          ينطبق هذا الحق بصرف النظر عن نسبة الاستخدام أو الوقت المنقضي منذ الشراء أو ما إذا
          كان المنتج مشترًى بسعر ترويجي. هذا الحق مكفول بموجب قانون حماية المستهلك البحريني
          رقم 35/2012 (المادتان 11 و12)، ولا يجوز استثناؤه بأي بند في هذه السياسة أو في
          الشروط والأحكام.
        </p>

        <h3>3.4 إيقاف المنتج</h3>
        <p>
          إذا أوقفت أمانات منتجاً أو خدمةً بعد عملية الشراء وقبل استهلاكها كلياً:
        </p>
        <ul>
          <li>تحصل على استرداد نسبي كامل عن الجزء غير المستهلك.</li>
          <li>
            توفِّر أمانات إشعاراً مسبقاً بمدة لا تقل عن 14 يوماً تقويمياً قبل إيقاف أي منتج أو
            خدمة سارية.
          </li>
        </ul>

        <h3>3.5 عطل الوصول التقني</h3>
        <p>
          إذا جرى خصم المبلغ بنجاح دون منح الوصول إلى المنتج أو الخدمة بسبب خطأ تقني من
          جانب أمانات:
        </p>
        <ul>
          <li>تعيد أمانات تفعيل الوصول خلال 24 ساعة من الإبلاغ عن المشكلة؛ أو</li>
          <li>تُصدر استرداداً كاملاً إذا تعذَّر استعادة الوصول خلال 24 ساعة.</li>
          <li>
            يجب الإبلاغ عن أعطال الوصول إلكترونياً عبر{" "}
            <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a> خلال
            48 ساعة من الشراء.
          </li>
        </ul>

        <h2>4. كيفية تقديم طلب الاسترداد — إلكترونياً فقط</h2>
        <p>
          يجب تقديم طلبات الاسترداد إلكترونياً عبر إحدى الطريقتين التاليتين:
        </p>
        <ul>
          <li>
            <strong>البريد الإلكتروني:</strong>{" "}
            <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a> (سطر
            الموضوع: &quot;طلب استرداد – [رقم مرجع الطلب]&quot;)
          </li>
          <li>
            <strong>داخل التطبيق:</strong> الإعدادات ← المساعدة والدعم ← طلب استرداد
          </li>
        </ul>
        <p>يُرجى تضمين المعلومات التالية في طلبك:</p>
        <ul>
          <li>الاسم الكامل وعنوان البريد الإلكتروني المسجَّل</li>
          <li>رقم مرجع الطلب</li>
          <li>اسم المنتج أو الخدمة أو وصفها</li>
          <li>سبب طلب الاسترداد</li>
          <li>
            دليل داعم عند الاقتضاء (مثل لقطة شاشة تبيِّن الخطأ أو عطل الوصول)
          </li>
        </ul>
        <p>
          <strong>مواعيد الاستجابة:</strong> تُقرِّر أمانات باستلام طلب الاسترداد خلال يومي
          عمل من الاستلام. يُبلَّغ بالقرار (موافقة أو رفض مع بيان الأسباب) خلال 5 أيام عمل.
          في حال رفض الطلب، يمكنك تصعيد المسألة وفقاً لما هو مبيَّن في البند 7.
        </p>
        <p>
          <strong>ملاحظة:</strong> لا تقبل أمانات طلبات الاسترداد عبر الهاتف أو بصورة
          شخصية. يجب تقديم جميع الطلبات من خلال القنوات الإلكترونية المذكورة أعلاه لضمان
          الاحتفاظ بسجل سليم.
        </p>

        <h2>5. مدة معالجة الاسترداد</h2>
        <p>
          بعد الموافقة على الاسترداد، يُعاد المبلغ إلى وسيلة الدفع الأصلية المستخدمة وقت
          الشراء:
        </p>
        <ul>
          <li>
            <strong>فيزا / ماستركارد (بطاقة ائتمان أو بطاقة مسبقة الدفع):</strong> 7–14 يوم
            عمل (وفقاً لأوقات معالجة البنك)
          </li>
          <li>
            <strong>BenefitPay:</strong> 3–7 أيام عمل
          </li>
        </ul>
        <p>
          تُعالج أمانات المبالغ المستردة المعتمدة على الفور. غير أن المدة الفعلية لظهور
          المبلغ في حسابك تتوقف على جهة الإصدار أو مزود الدفع الخاص بك، وهو ما يخرج عن نطاق
          سيطرة أمانات. تُحوَّل المبالغ المستردة إلى وسيلة الدفع الأصلية حصراً؛ ولا يمكن
          تحويلها إلى بطاقة أو حساب أو وسيلة دفع مغايرة.
        </p>

        <h2>6. وسائل الدفع والحالات الخاصة</h2>
        <h3>6.1 وسائل الدفع المقبولة</h3>
        <p>
          يقبل متجر أمانات الإلكتروني (eCart) فيزا وماستركارد وBenefitPay. الدفع عند
          الاستلام غير متاح. يجب إتمام جميع عمليات الشراء عبر وسائل الدفع الإلكترونية الآمنة
          المذكورة أعلاه.
        </p>
        <h3>6.2 النزاعات المتعلقة بالبطاقة (رد المدفوعات)</h3>
        <p>
          إذا بادرت بتقديم طلب رد مدفوعات (chargeback) لدى جهة إصدار بطاقتك أو عبر
          BenefitPay دون التواصل المسبق مع أمانات، سنتعامل مع النزاع عبر مزود الدفع المعني.
          نحثُّ العملاء بشدة على التواصل مباشرةً مع أمانات عبر{" "}
          <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a> قبل تقديم
          طلب رد المدفوعات، إذ قد تؤدي الإجراءات المتوازية إلى تأخير الحل.
        </p>
        <h3>6.3 المعاملات الفاشلة</h3>
        <p>
          إذا خُصم مبلغ من حسابك دون استلامك رسالة تأكيد الطلب خلال 30 دقيقة، يُرجى الإبلاغ
          فوراً إلكترونياً عبر{" "}
          <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a>. ستُجري
          أمانات التحقيق وستؤكد طلبك أو تُصدر استرداداً كاملاً خلال 3 أيام عمل. لا تتحمل
          أمانات مسؤولية التأخيرات الناجمة عن البنك أو جهة إصدار البطاقة في إلغاء المعاملة
          الفاشلة.
        </p>

        <h2>7. تسوية النزاعات والتصعيد</h2>
        <p>
          إذا كنت غير راضٍ عن نتيجة طلب استردادك، يمكنك تصعيد شكواك وفق المراحل التالية.
          يجب استنفاد كل مرحلة قبل الانتقال إلى ما يليها:
        </p>

        <h3>المرحلة الأولى — الحل الداخلي للشكاوى (جهة الاتصال الأولى)</h3>
        <p>
          قدِّم شكواك مباشرةً إلى فريق حل الشكاوى لدى أمانات. نحن ملتزمون بمعالجة جميع
          الشكاوى بصورة عادلة وفي الوقت المناسب.
        </p>
        <ul>
          <li>
            <strong>النموذج الإلكتروني / البريد الإلكتروني:</strong>{" "}
            <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a> |{" "}
            <strong>الهاتف:</strong>{" "}
            <a href="tel:+97336961804">+973 3696 1804</a>
          </li>
          <li>
            <strong>سطر الموضوع:</strong> &quot;شكوى رسمية – [الاسم] – [مرجع الطلب]&quot;
          </li>
          <li>
            <strong>مواعيد الاستجابة:</strong> إقرار الاستلام خلال يومي عمل؛ الحل خلال 5
            أيام عمل.
          </li>
        </ul>
        <p>
          إذا لم تُحسم شكواك بما يرضيك في المرحلة الأولى خلال 5 أيام عمل، يمكنك التصعيد إلى
          الجهة الخارجية المختصة في المرحلة الثانية.
        </p>

        <h3>
          المرحلة الثانية — وزارة الصناعة والتجارة والسياحة – قسم التجارة الإلكترونية (جهة
          خارجية إلزامية — رخصة التجارة الإلكترونية)
        </h3>
        <p>
          للشكاوى المتعلقة بمعاملات التجارة الإلكترونية أو أوصاف المنتجات أو التسليم أو رفض
          الاسترداد، يمكنك التصعيد إلى وزارة الصناعة والتجارة والسياحة (MOICT) — الجهة
          المرخِّصة لنشاط أمانات الإلكتروني (النشاط 4791، السجل التجاري 178727-1). يُعد ذكر
          الوزارة في هذه المرحلة إفصاحاً إلزامياً بموجب رخصة التجارة الإلكترونية الصادرة
          لأمانات.
        </p>
        <ul>
          <li>
            <strong>البريد الإلكتروني:</strong>{" "}
            <a href="mailto:ecommerce@moic.gov.bh">ecommerce@moic.gov.bh</a>
          </li>
          <li>
            <strong>هاتف:</strong> <a href="tel:17359008">17359008</a>
          </li>
          <li>
            <strong>مديرية حماية المستهلك:</strong>{" "}
            <a href="tel:17574969">17574969</a>
          </li>
        </ul>

        <h3>
          المرحلة الثالثة — مصرف البحرين المركزي (جهة خارجية إلزامية — الخدمات المالية
          المنظَّمة)
        </h3>
        <p>
          للشكاوى المتعلقة بالخدمات المالية الخاضعة لإشراف مصرف البحرين المركزي (بما في ذلك
          أي نشاط مرخَّص من CBB تمارسه أمانات)، يمكنك التصعيد إلى وحدة حماية المستهلك بمصرف
          البحرين المركزي. يُعد الإفصاح عن هذا الحق إلزامياً بموجب وحدة حماية المستهلك الصادرة
          عن مصرف البحرين المركزي.
        </p>
        <ul>
          <li>
            <strong>الموقع الإلكتروني:</strong>{" "}
            <a href="https://www.cbb.gov.bh" target="_blank" rel="noopener noreferrer">
              www.cbb.gov.bh
            </a>
          </li>
          <li>
            <strong>بوابة الشكاوى الإلكترونية:</strong>{" "}
            <a
              href="https://complaints.cbb.gov.bh"
              target="_blank"
              rel="noopener noreferrer"
            >
              complaints.cbb.gov.bh
            </a>
          </li>
          <li>
            <strong>هاتف:</strong>{" "}
            <a href="tel:+97317547789">+973 17547789</a>
          </li>
        </ul>
        <p>
          <strong>ملاحظة بشأن البيانات الشخصية:</strong> لأي استفسار يتعلق تحديداً بمعالجة
          بياناتك الشخصية أثناء معالجة طلب استردادك، يُرجى التواصل مع مسؤول الخصوصية على{" "}
          <a href="mailto:privacy@amanatfintech.com">privacy@amanatfintech.com</a> أو مراجعة
          سياسة الخصوصية على{" "}
          <Link to="/privacy-policy">www.amanatfintech.com/privacy-policy</Link>.
        </p>

        <h2>8. القانون الحاكم</h2>
        <p>
          تخضع هذه السياسة لقوانين مملكة البحرين، بما في ذلك قانون حماية المستهلك رقم
          35/2012 والقانون المدني ولوائح مصرف البحرين المركزي. تخضع أي نزاعات تنشأ عن هذه
          السياسة للاختصاص القضائي الحصري للمحاكم المختصة في مملكة البحرين.
        </p>

        <h2>9. التعديلات على هذه السياسة</h2>
        <p>
          تحتفظ أمانات بالحق في تحديث هذه السياسة في أي وقت لتعكس التغييرات في منتجاتنا
          وخدماتنا أو القانون المعمول به. يُبلَّغ بالتغييرات الجوهرية عبر الموقع الإلكتروني
          وإشعار داخل التطبيق. يتضمن الإصدار المحدَّث تاريخ &quot;آخر تحديث&quot; جديداً. يُعد
          استمرار استخدامك لخدمات أمانات بعد تاريخ السريان المعدَّل قبولاً للسياسة المنقَّحة.
        </p>

        <h2>10. تواصل معنا</h2>
        <p>
          <strong>شركة كولكا سيرفيسز ذ.م.م (أمانات)</strong>
        </p>
        <p>
          السجل التجاري رقم 178727-1 | 302، الطابق الثالث، مبنى أركابيتا، خليج البحرين
          للتقنية المالية، شارع 4612، بلوك 346، المنامة، مملكة البحرين
        </p>
        <ul>
          <li>
            <strong>البريد الإلكتروني:</strong>{" "}
            <a href="mailto:support@amanatfintech.com">support@amanatfintech.com</a> |{" "}
            <a href="mailto:info@amanatfintech.com">info@amanatfintech.com</a>
          </li>
          <li>
            <strong>الموقع الإلكتروني:</strong>{" "}
            <a
              href="https://www.amanatfintech.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.amanatfintech.com
            </a>{" "}
            | <strong>متجر eCart الإلكتروني:</strong>{" "}
            <a
              href="https://ecart.amanatfintech.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              ecart.amanatfintech.com
            </a>
          </li>
          <li>
            <strong>ساعات العمل:</strong> الأحد – الخميس، من 9:00 صباحاً حتى 5:00 مساءً
            (بتوقيت المملكة العربية السعودية)
          </li>
        </ul>

        <h2>11. البند ثنائي اللغة / Bilingual Clause</h2>
        <p>
          تم إعداد سياسة الإرجاع والاسترداد هذه باللغتين الإنجليزية والعربية. في حالة أي
          تناقض أو تعارض بين النسختين الإنجليزية والعربية، تسود النسخة العربية وفقاً لقوانين
          مملكة البحرين.
        </p>
        <p className="legal-bilingual-en" dir="ltr">
          This Return &amp; Refund Policy is prepared in both English and Arabic.
          <br />
          In the event of any inconsistency or conflict between the English and Arabic
          versions, the Arabic version shall prevail in accordance with the laws of the
          Kingdom of Bahrain.
        </p>
      </div>
    </LegalPageLayout>
  );
};

export default RefundPolicy;
